/**
 * FERP Portal - Saga List Page
 *
 * Saga orchestrations for distributed transactions.
 * Displays saga definitions from backend via useSagaDefinitionsQuery.
 *
 * CONSTRAINTS:
 * - Mantine v8.x only
 * - Data from backend via useSagaDefinitionsQuery
 * - Multi-tenant via useTenantId()
 * - Dumb Frontend: All data from backend, no hardcoded values
 */

import { useMemo, useState } from 'react';
import { useTerminology } from '@ferp/workflow-forms';
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Stack,
  Badge,
  Table,
  Paper,
  ThemeIcon,
  Alert,
  Button,
  Loader,
  TextInput,
} from '@mantine/core';
import {
  IconArrowsShuffle2,
  IconAlertTriangle,
  IconRefresh,
  IconSearch,
} from '@tabler/icons-react';
import { useTenantId } from '@/auth';
import { useSagaDefinitionsQuery } from '@/generated/graphql';

export function SagaListPage() {
  const { translate } = useTerminology();
  const tenantId = useTenantId();
  const [searchQuery, setSearchQuery] = useState('');

  const { data, loading, error, refetch } = useSagaDefinitionsQuery();

  const sagas = useMemo(() => {
    return data?.sagaDefinitions ?? [];
  }, [data]);

  const filteredSagas = useMemo(() => {
    if (!searchQuery) return sagas;
    const query = searchQuery.toLowerCase();
    return sagas.filter((s) =>
      s.sagaId.toLowerCase().includes(query) ||
      s.description?.toLowerCase().includes(query) ||
      s.version?.toLowerCase().includes(query)
    );
  }, [sagas, searchQuery]);

  if (!tenantId) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertTriangle size={16} />} color="yellow" title="No Tenant Selected">
          Please select a tenant to view sagas.
        </Alert>
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertTriangle size={16} />} color="red" title="Error Loading Sagas">
          {error.message}
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Title order={2}>{translate('transaction')} Orchestrations</Title>
            <Text c="dimmed" size="sm">
              Distributed {translate('transaction').toLowerCase()}s with compensating rollback actions
            </Text>
          </div>
          <Button
            leftSection={<IconRefresh size={16} />}
            variant="light"
            onClick={() => refetch()}
            loading={loading}
          >
            Refresh
          </Button>
        </Group>

        {/* Stats */}
        <Card withBorder p="md" style={{ maxWidth: 200 }}>
          <Group gap="xs">
            <ThemeIcon variant="light" size="sm" color="cyan">
              <IconArrowsShuffle2 size={14} />
            </ThemeIcon>
            <div>
              <Text size="xs" c="dimmed">Definitions</Text>
              <Text size="xl" fw={700}>{sagas.length}</Text>
            </div>
          </Group>
        </Card>

        {/* Search */}
        <TextInput
          placeholder="Search sagas..."
          leftSection={<IconSearch size={16} />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.currentTarget.value)}
        />

        {/* Sagas Table */}
        {loading ? (
          <Group justify="center" py="xl">
            <Loader />
          </Group>
        ) : (
          <Paper withBorder>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Saga ID</Table.Th>
                  <Table.Th>Description</Table.Th>
                  <Table.Th>Version</Table.Th>
                  <Table.Th>Metadata</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filteredSagas.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={4}>
                      <Text ta="center" c="dimmed" py="md">
                        No saga definitions found
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  filteredSagas.map((saga) => (
                    <Table.Tr key={saga.sagaId}>
                      <Table.Td>
                        <Text size="sm" fw={500}>{saga.sagaId}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed" lineClamp={1}>
                          {saga.description ?? 'No description'}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge variant="outline" size="sm">v{saga.version}</Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" c="dimmed">
                          {Object.keys(saga.metadata ?? {}).length} keys
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ))
                )}
              </Table.Tbody>
            </Table>
          </Paper>
        )}

        <Badge variant="light" color="cyan" style={{ alignSelf: 'flex-start' }}>
          EXECUTE Phase
        </Badge>
      </Stack>
    </Container>
  );
}

export default SagaListPage;
